import {useTranslation} from "react-i18next";
import useDownloadState from "../../../api/utilities/useDownloadState.js";
import {useContext, useEffect, useMemo, useState} from "react";
import {ProjectIntegrationsApi} from "../../../api/authority/generated/index";
import FormError from "../../../components/form/FormError.jsx";
import {GlobalContext} from "../../../utils/context/GlobalContextProvider.jsx";
import {faRefresh} from "@fortawesome/free-solid-svg-icons/faRefresh";
import {faPlus} from "@fortawesome/free-solid-svg-icons";
import ThirdPartyServiceRow from "./ThirdPartyServiceRow.jsx";
import {useNavigate, useSearchParams} from "react-router-dom";
import NarrowSecureLayout from "../../../layout/NarrowSecureLayout.jsx";
import BrandLogo from "../../../utils/brands/BrandLogo.jsx";
import ThirdPartyServiceEditForm from "./ThirdPartyServiceEditForm.jsx";
import Button from "../../../components/Button.jsx";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import LoadingComponent from "../../../components/LoadingComponent.jsx";
import {useBrand} from "../../../utils/brands/branding.js";
import SupportLink from "../../../components/SupportLink.jsx";
import ActionButton from "../../../components/ActionButton.jsx";

export default function ThirdPartyServiceIntegrations({project}) {

    let {t} = useTranslation();
    let navigate = useNavigate();

    let [state, setDownloading, setResult, setError] = useDownloadState();
    let [refresh, setRefresh] = useState(0);

    let {getAuthorityConfiguration} = useContext(GlobalContext);

    let [searchParam, setSearchParams] = useSearchParams();

    useEffect(() => {

        if (state.downloading) {
            return;
        }

        setDownloading(true);

        new ProjectIntegrationsApi(getAuthorityConfiguration())
            .listProjectIntegrations({
                projectId: project.id
            })
            .then((r) => setResult(r))
            .catch((e) => setError(e))
            .finally(() => setDownloading(false));

    }, [project.id, refresh]);

    let selectedProjectIntegration = useMemo(() => {
        let editIntegrationId = searchParam.get('integrationId');
        for (let integration of state.result ?? []) {
            // noinspection JSUnresolvedReference
            if (integration.id === editIntegrationId) {
                return integration;
            }
        }

        return null;

    }, [project.id, refresh, searchParam, searchParam.get('integrationId'), state.result]);

    let microsoftOAuthAlert = useMemo(() => {
        const errorMessage = searchParam.get('microsoftOAuthErrorMessage');

        if (!errorMessage) {
            return null;
        }

        const errorCode = searchParam.get('microsoftOAuthErrorCode');

        return {
            id: 'microsoft-oauth-error',
            type: 'danger',
            showClose: false,
            content: errorCode
                ? `${errorMessage} (${errorCode})`
                : errorMessage,
        };
    }, [searchParam]);

    // noinspection JSUnresolvedReference
    let brand = useBrand(selectedProjectIntegration?.name);

    if (selectedProjectIntegration !== null && brand !== null) {
        //let brand = Branding[selectedProjectIntegration.name];

        return <NarrowSecureLayout
            title={<div className={'inline-flex gap-3'}>
                <BrandLogo brand={brand} className={'mx-aut my-auto'}/>
                <div className={'mx-aut my-auto'}>{brand?.name}</div>
            </div>
            }
            actions={<>
                {brand.helpArticleId &&
                    <SupportLink
                        articleId={brand.helpArticleId}
                        title={t('support.help-for-integration', {integrationName: brand.name})
                    }/>
                }
            </>}
        >
            <ThirdPartyServiceEditForm project={project} brand={brand} projectIntegration={selectedProjectIntegration}/>
        </NarrowSecureLayout>;
    }

    return (
        <NarrowSecureLayout
            title={t('project.3rd-party-integrations')}
            actions={<>
                <div className="flex gap-1 justify-center items-center">
                    <ActionButton icon={'plus'} onClick={() => navigate('/project/' + project.id + '/settings/integrations/create')} />
                    <ActionButton icon={'sync'} onClick={() => setRefresh((refresh) => refresh + 1)} />
                </div>
            </>}
            alerts={[
                microsoftOAuthAlert,
                {
                    id: 'utm-pdf-settings',
                    type: 'primary',
                    showClose: false,
                    className: '',
                    content: (
                        <span>
                            Pro párování objednávek s reklamními kampaněmi je klíčové správné{' '}
                            <a
                                href="https://datixo.com/documents/UTM_Parameters_Setup.pdf"
                                target="_blank"
                                rel="noreferrer"
                                className="font-medium text-violet-700 underline decoration-violet-300 underline-offset-2 hover:text-violet-800"
                            >
                                nastavení UTM parametrů
                            </a>
                            .
                        </span>
                    ),
                },
            ]}
        >
            <LoadingComponent loading={state.downloading}>
                {state.error && <FormError error={state.error}/>}
                {state.result && state.result.length === 0 &&
                    <div className={'text-xs text-center italic'}>{t('project.no-integrations-found')}</div>
                }
                {state.result && state.result.length > 0 && state.result.map((projectIntegration) => {
                    return <ThirdPartyServiceRow
                        project={project}
                        projectIntegration={projectIntegration}
                        onDeleted={() => setRefresh((refresh) => refresh + 1)}
                        key={projectIntegration.id}
                    />
                })}
            </LoadingComponent>
        </NarrowSecureLayout>
    );
}
